import {useGetPlanFishGroupProperties} from 'hooks/swr';
import {Tags} from './Tags';
import useParseURL from 'hooks/useParseURL';
import backendCommunicator from 'services/backendCommunicator';
import {mutateList} from 'hooks/swr/swrHookFactory';
import {SWRKeyEnum} from 'interfaces';
import {idToNameMap} from 'utils/maps';
import {type ComponentProps, useMemo} from 'react';
import {useFlags} from 'launchdarkly-react-client-sdk';

export const FishGroupPropertiesTags = ({
  selectedTags,
  onSelectionChange,
  onAfterCreate,
  translationKeys,
  disabled,
  ...rest
}: {
  selectedTags: string[];
  onSelectionChange: (newSelectedTags: string[]) => void;
  onAfterCreate: (id: string) => void;
  disabled?: boolean;
} & Omit<ComponentProps<typeof Tags>, 'onCreate' | 'tagsMap' | 'selectedTagIds'>) => {
  const {planId} = useParseURL();
  const {fishGroupPropertiesEnabled} = useFlags();
  const {data: fishGroupProperties} = useGetPlanFishGroupProperties({
    resourceId: fishGroupPropertiesEnabled ? planId : undefined,
  });
  const tagsMap = useMemo(() => idToNameMap(fishGroupProperties ?? []), [fishGroupProperties]);

  if (!fishGroupPropertiesEnabled) {
    return null;
  }

  return (
    <Tags
      selectedTagIds={selectedTags}
      tagsMap={tagsMap}
      translationKeys={{
        addNewLabel: 'addFishGroupProperty',
        ...translationKeys,
      }}
      onCreate={async (name) => {
        const newProperty = {name, planId};
        const response = await mutateList({
          key: [planId, SWRKeyEnum.GET_PLAN_FISH_GROUP_PROPERTIES],
          updateFunction: backendCommunicator.addFishGroupProperty(newProperty),
          newEntry: {id: `FAKE_ID_${Date.now()}`, ...newProperty},
          revalidate: true,
        });
        if (response?.data) {
          onAfterCreate(response.data.id);
        }
      }}
      onSelectionChange={onSelectionChange}
      dataTestIdPrefix="fish-group-preoperties-"
      disabled={disabled}
      {...rest}
    />
  );
};
